import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Mail,
    Sparkles,
    LogOut,
    Briefcase,
    Pencil,
    Plus,
    Search,
    ArrowUpRight,
    FileText,
    MapPin,
    EyeOff,
    Eye,
    Settings,
    Megaphone,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useEditMode } from "../contexts/EditModeContext";
import {
    APPLICATION_STATUSES,
    CONTACT_STATUSES,
    LEAD_STATUSES,
    getStatusInfo,
    formatDateTime,
} from "../lib/adminConstants";
import ApplicationDetailModal from "../components/ApplicationDetailModal";
import ContactDetailModal from "../components/ContactDetailModal";
import JobFormModal from "../components/JobFormModal";
import AccountSettingsModal from "../components/AccountSettingsModal";
import LeadDetailModal from "../components/LeadDetailModal";
import LeadMagnetEditor from "../components/LeadMagnetEditor";

const TABS = [
    { key: "stats", label: "Übersicht", icon: LayoutDashboard },
    { key: "jobs", label: "Stellen", icon: Briefcase },
    { key: "applications", label: "Bewerbungen", icon: FileText },
    { key: "contacts", label: "Kontakte", icon: Mail },
    { key: "leads", label: "Leads", icon: Sparkles },
    { key: "lead-magnet", label: "Lead-Magnet", icon: Megaphone },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const { setEditMode } = useEditMode();
    const [active, setActive] = useState("stats");

    const [stats, setStats] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [applications, setApplications] = useState([]);
    const [leads, setLeads] = useState([]);
    const [jobs, setJobs] = useState([]);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const [activeApp, setActiveApp] = useState(null);
    const [activeContact, setActiveContact] = useState(null);
    const [activeJob, setActiveJob] = useState(null);
    const [activeLead, setActiveLead] = useState(null);
    const [jobModalOpen, setJobModalOpen] = useState(false);
    const [accountModalOpen, setAccountModalOpen] = useState(false);

    const loadAll = () =>
        Promise.all([
            api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}),
            api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {}),
            api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {}),
            api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}),
            api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {}),
        ]);

    useEffect(() => {
        loadAll();
    }, []);

    // Search/filter helpers
    const matchSearch = (text) => !search || text.toLowerCase().includes(search.toLowerCase());

    const filteredApps = useMemo(() => {
        return applications.filter((a) => {
            if (statusFilter !== "all" && (a.status || "new") !== statusFilter) return false;
            return matchSearch(`${a.name} ${a.email} ${a.position}`);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications, search, statusFilter]);

    const filteredContacts = useMemo(() => {
        return contacts.filter((c) => {
            if (statusFilter !== "all" && (c.status || "new") !== statusFilter) return false;
            return matchSearch(`${c.name} ${c.email} ${c.company || ""} ${c.message}`);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contacts, search, statusFilter]);

    const filteredLeads = useMemo(() => {
        return leads.filter((l) => matchSearch(`${l.name || ""} ${l.email} ${l.source}`));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leads, search]);

    const filteredJobs = useMemo(() => {
        return jobs.filter((j) => matchSearch(`${j.title} ${j.location} ${(j.tags || []).join(" ")}`));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs, search]);

    // Reset filters when changing tabs
    useEffect(() => {
        setSearch("");
        setStatusFilter("all");
    }, [active]);

    return (
        <div className="min-h-screen bg-aimploy-bg flex" data-testid="admin-dashboard">
            {/* SIDEBAR */}
            <aside className="hidden lg:flex w-72 bg-aimploy-dark text-white flex-col p-6 sticky top-0 h-screen">
                <Link to="/" className="flex items-center gap-2 mb-12">
                    <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center">
                        <span className="font-display font-bold text-aimploy-navy text-lg">a.</span>
                    </div>
                    <span className="font-display font-bold text-xl">aimploy</span>
                </Link>

                <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold mb-3 px-3">Admin</div>
                <nav className="flex-1 space-y-1">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setActive(t.key)}
                            data-testid={`admin-tab-${t.key}`}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                                active === t.key
                                    ? "bg-aimploy-violet text-white"
                                    : "text-white/70 hover:bg-white/5 hover:text-white"
                            }`}
                        >
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </nav>

                <div className="border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between gap-2 px-4 mb-3">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{user?.name}</div>
                            <div className="text-xs text-white/50 truncate">{user?.email}</div>
                        </div>
                        <button
                            onClick={() => setAccountModalOpen(true)}
                            data-testid="admin-open-settings"
                            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 flex-shrink-0"
                            aria-label="Konto-Einstellungen"
                            title="Konto-Einstellungen"
                        >
                            <Settings className="w-4 h-4" />
                        </button>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white"
                    >
                        <LogOut className="w-4 h-4" /> Abmelden
                    </button>
                </div>
            </aside>

            {/* MAIN */}
            <main className="flex-1 min-w-0">
                {/* Mobile header */}
                <div className="lg:hidden bg-aimploy-dark text-white p-4 flex items-center justify-between sticky top-0 z-10">
                    <Link to="/" className="font-display font-bold text-lg">aimploy</Link>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setAccountModalOpen(true)}
                            data-testid="admin-mobile-settings"
                            className="text-white/70 p-2"
                            aria-label="Konto"
                        >
                            <Settings className="w-5 h-5" />
                        </button>
                        <button onClick={logout} className="text-white/70 p-2" aria-label="Abmelden"><LogOut className="w-5 h-5" /></button>
                    </div>
                </div>
                <div className="lg:hidden flex gap-2 p-4 overflow-x-auto bg-white border-b border-black/5 sticky top-[60px] z-10">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setActive(t.key)}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                                active === t.key ? "bg-aimploy-navy text-white" : "bg-aimploy-bg text-aimploy-navy"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="p-6 lg:p-12">
                    {active === "stats" && (
                        <StatsView
                            user={user}
                            stats={stats}
                            applications={applications}
                            contacts={contacts}
                            jobs={jobs}
                            setActive={setActive}
                            setEditMode={setEditMode}
                            openApp={(a) => setActiveApp(a)}
                            openContact={(c) => setActiveContact(c)}
                            openNewJob={() => { setActiveJob(null); setJobModalOpen(true); }}
                        />
                    )}

                    {active === "jobs" && (
                        <JobsView
                            jobs={filteredJobs}
                            search={search}
                            setSearch={setSearch}
                            onOpen={(j) => { setActiveJob(j); setJobModalOpen(true); }}
                            onNew={() => { setActiveJob(null); setJobModalOpen(true); }}
                        />
                    )}

                    {active === "applications" && (
                        <ApplicationsView
                            applications={filteredApps}
                            total={applications.length}
                            search={search}
                            setSearch={setSearch}
                            statusFilter={statusFilter}
                            setStatusFilter={setStatusFilter}
                            onOpen={(a) => setActiveApp(a)}
                        />
                    )}

                    {active === "contacts" && (
                        <ContactsView
                            contacts={filteredContacts}
                            total={contacts.length}
                            search={search}
                            setSearch={setSearch}
                            statusFilter={statusFilter}
                            setStatusFilter={setStatusFilter}
                            onOpen={(c) => setActiveContact(c)}
                        />
                    )}

                    {active === "leads" && (
                        <LeadsView
                            leads={filteredLeads}
                            total={leads.length}
                            search={search}
                            setSearch={setSearch}
                            onOpen={(l) => setActiveLead(l)}
                        />
                    )}

                    {active === "lead-magnet" && <LeadMagnetEditor />}
                </div>
            </main>

            {/* MODALS */}
            <ApplicationDetailModal
                application={activeApp}
                open={!!activeApp}
                onOpenChange={(o) => !o && setActiveApp(null)}
                onUpdated={(updated) => {
                    setApplications((arr) => arr.map((a) => (a.id === updated.id ? updated : a)));
                }}
            />
            <ContactDetailModal
                contact={activeContact}
                open={!!activeContact}
                onOpenChange={(o) => !o && setActiveContact(null)}
                onUpdated={(updated) => {
                    setContacts((arr) => arr.map((c) => (c.id === updated.id ? updated : c)));
                }}
            />
            <LeadDetailModal
                lead={activeLead}
                open={!!activeLead}
                onOpenChange={(o) => !o && setActiveLead(null)}
                onUpdated={(updated) => {
                    setLeads((arr) => arr.map((l) => (l.id === updated.id ? updated : l)));
                    setActiveLead(updated);
                }}
            />
            <JobFormModal
                job={activeJob}
                open={jobModalOpen}
                onOpenChange={setJobModalOpen}
                onSaved={loadAll}
            />
            <AccountSettingsModal
                user={user}
                open={accountModalOpen}
                onOpenChange={setAccountModalOpen}
            />
        </div>
    );
}

// ---------------- VIEWS ----------------

function StatsView({ user, stats, applications, contacts, jobs, setActive, setEditMode, openApp, openContact, openNewJob }) {
    const newApps = applications.filter((a) => (a.status || "new") === "new").length;
    const newContacts = contacts.filter((c) => (c.status || "new") === "new").length;
    const publishedJobs = jobs.filter((j) => j.is_published !== false).length;

    return (
        <div data-testid="admin-stats-view">
            <h1 className="font-display text-4xl lg:text-5xl font-bold mb-2">Übersicht</h1>
            <p className="text-aimploy-muted mb-10">Willkommen zurück, {user?.name}.</p>

            {/* Quick actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                <div className="bg-aimploy-navy text-white rounded-2xl p-6 lg:p-8 relative overflow-hidden">
                    <div className="absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-aimploy-violet/30 blur-3xl" />
                    <div className="relative">
                        <div className="flex items-start gap-4 mb-5">
                            <div className="h-12 w-12 rounded-xl bg-aimploy-violet flex items-center justify-center flex-shrink-0">
                                <Pencil className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-display text-xl font-bold">Inhalts-Editor</h3>
                                <p className="text-white/70 text-sm mt-1">Texte direkt auf der Website bearbeiten — wie WordPress.</p>
                            </div>
                        </div>
                        <Link
                            to="/"
                            onClick={() => setEditMode(true)}
                            data-testid="admin-cms-launch"
                            className="inline-flex items-center justify-center gap-2 bg-white text-aimploy-navy px-5 py-2.5 rounded-full text-sm font-semibold hover:scale-[1.02] transition-all"
                        >
                            Bearbeiten starten <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
                <div className="bg-white rounded-2xl p-6 lg:p-8 border border-black/5">
                    <div className="flex items-start gap-4 mb-5">
                        <div className="h-12 w-12 rounded-xl bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center flex-shrink-0">
                            <Plus className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-display text-xl font-bold">Neue Stelle anlegen</h3>
                            <p className="text-aimploy-muted text-sm mt-1">Direkt auf der Kandidaten-Seite veröffentlichen.</p>
                        </div>
                    </div>
                    <button
                        onClick={openNewJob}
                        data-testid="admin-quick-new-job"
                        className="inline-flex items-center justify-center gap-2 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                    >
                        Stelle anlegen <ArrowUpRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                    { label: "Bewerbungen", value: stats?.applications ?? "—", sub: `${newApps} neu`, icon: FileText },
                    { label: "Kontakte", value: stats?.contacts ?? "—", sub: `${newContacts} neu`, icon: Mail },
                    { label: "Leads", value: stats?.leads ?? "—", sub: "gesamt", icon: Sparkles },
                    { label: "Aktive Stellen", value: publishedJobs, sub: `${jobs.length} gesamt`, icon: Users },
                ].map((s) => (
                    <div key={s.label} className="bg-white rounded-2xl p-6 border border-black/5">
                        <div className="flex items-center justify-between mb-6">
                            <div className="h-10 w-10 rounded-xl bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center">
                                <s.icon className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="font-display text-4xl font-bold">{s.value}</div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted mt-2 font-semibold">{s.label}</div>
                        <div className="text-xs text-aimploy-violet mt-1 font-semibold">{s.sub}</div>
                    </div>
                ))}
            </div>

            {/* Recent activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-8">
                <RecentList
                    title="Letzte Bewerbungen"
                    onAll={() => setActive("applications")}
                    items={applications.slice(0, 5).map((a) => ({
                        key: a.id,
                        primary: a.name,
                        secondary: a.position,
                        meta: formatDateTime(a.created_at),
                        status: getStatusInfo(APPLICATION_STATUSES, a.status || "new"),
                        onClick: () => openApp(a),
                    }))}
                />
                <RecentList
                    title="Letzte Kontakte"
                    onAll={() => setActive("contacts")}
                    items={contacts.slice(0, 5).map((c) => ({
                        key: c.id,
                        primary: c.name,
                        secondary: c.email,
                        meta: c.user_type === "company" ? "Unternehmen" : "Kandidat",
                        status: getStatusInfo(CONTACT_STATUSES, c.status || "new"),
                        onClick: () => openContact(c),
                    }))}
                />
            </div>
        </div>
    );
}

function RecentList({ title, onAll, items }) {
    return (
        <div className="bg-white rounded-2xl p-6 border border-black/5">
            <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-lg font-bold">{title}</h3>
                <button onClick={onAll} className="text-sm text-aimploy-violet font-semibold inline-flex items-center gap-1">
                    Alle <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
            </div>
            <div className="space-y-1">
                {items.map((it) => (
                    <button
                        key={it.key || it.primary}
                        onClick={it.onClick}
                        className="w-full flex items-center justify-between gap-3 text-sm py-3 px-3 -mx-3 rounded-xl hover:bg-aimploy-bg transition-colors text-left"
                    >
                        <div className="min-w-0">
                            <div className="font-semibold truncate">{it.primary}</div>
                            <div className="text-xs text-aimploy-muted truncate">{it.secondary}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${it.status.color}`}>
                                {it.status.label}
                            </span>
                            <span className="text-xs text-aimploy-muted">{it.meta}</span>
                        </div>
                    </button>
                ))}
                {items.length === 0 && <div className="text-sm text-aimploy-muted py-6 text-center">Noch keine Einträge.</div>}
            </div>
        </div>
    );
}

function SearchBar({ search, setSearch, placeholder, statuses, statusFilter, setStatusFilter, action }) {
    return (
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-aimploy-muted" />
                <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={placeholder || "Suchen…"}
                    data-testid="admin-search"
                    className="w-full bg-white border border-black/5 rounded-xl pl-11 pr-4 py-3 text-sm"
                />
            </div>
            {statuses && (
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="admin-status-filter"
                    className="bg-white border border-black/5 rounded-xl px-4 py-3 text-sm font-medium min-w-[160px]"
                >
                    <option value="all">Alle Status</option>
                    {statuses.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>
            )}
            {action}
        </div>
    );
}

function ApplicationsView({ applications, total, search, setSearch, statusFilter, setStatusFilter, onOpen }) {
    return (
        <div data-testid="admin-applications-view">
            <h1 className="font-display text-4xl lg:text-5xl font-bold mb-2">Bewerbungen</h1>
            <p className="text-aimploy-muted mb-8">{total} Einträge gesamt · {applications.length} angezeigt</p>
            <SearchBar
                search={search}
                setSearch={setSearch}
                placeholder="Name, E-Mail oder Position…"
                statuses={APPLICATION_STATUSES}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
            />
            <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-black/5 text-xs uppercase tracking-[0.15em] text-aimploy-muted bg-aimploy-bg">
                                <th className="text-left p-4 font-semibold">Name</th>
                                <th className="text-left p-4 font-semibold hidden md:table-cell">Position</th>
                                <th className="text-left p-4 font-semibold">Status</th>
                                <th className="text-left p-4 font-semibold hidden lg:table-cell">Datum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map((a) => {
                                const s = getStatusInfo(APPLICATION_STATUSES, a.status || "new");
                                return (
                                    <tr
                                        key={a.id}
                                        onClick={() => onOpen(a)}
                                        data-testid={`admin-app-row-${a.id}`}
                                        className="border-b border-black/5 last:border-0 hover:bg-aimploy-bg cursor-pointer transition-colors"
                                    >
                                        <td className="p-4">
                                            <div className="font-semibold">{a.name}</div>
                                            <div className="text-xs text-aimploy-muted">{a.email}</div>
                                        </td>
                                        <td className="p-4 hidden md:table-cell">{a.position}</td>
                                        <td className="p-4">
                                            <span className={`inline-block text-xs px-2.5 py-1 rounded-full font-semibold ${s.color}`}>
                                                {s.label}
                                            </span>
                                        </td>
                                        <td className="p-4 text-aimploy-muted hidden lg:table-cell">{formatDateTime(a.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {applications.length === 0 && <div className="p-12 text-center text-aimploy-muted">Keine Bewerbungen gefunden.</div>}
                </div>
            </div>
        </div>
    );
}

function ContactsView({ contacts, total, search, setSearch, statusFilter, setStatusFilter, onOpen }) {
    return (
        <div data-testid="admin-contacts-view">
            <h1 className="font-display text-4xl lg:text-5xl font-bold mb-2">Kontakte</h1>
            <p className="text-aimploy-muted mb-8">{total} Anfragen gesamt · {contacts.length} angezeigt</p>
            <SearchBar
                search={search}
                setSearch={setSearch}
                placeholder="Name, E-Mail, Unternehmen…"
                statuses={CONTACT_STATUSES}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
            />
            <div className="space-y-3">
                {contacts.map((c) => {
                    const s = getStatusInfo(CONTACT_STATUSES, c.status || "new");
                    return (
                        <button
                            key={c.id}
                            onClick={() => onOpen(c)}
                            data-testid={`admin-contact-row-${c.id}`}
                            className="w-full text-left bg-white rounded-2xl p-5 lg:p-6 border border-black/5 hover:border-aimploy-violet/30 hover:-translate-y-0.5 transition-all"
                        >
                            <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="font-display text-lg font-bold truncate">{c.name}</div>
                                    <div className="text-sm text-aimploy-muted truncate">{c.email}{c.company && ` · ${c.company}`}</div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-xs px-2.5 py-1 rounded-full bg-aimploy-violet/10 text-aimploy-violet font-semibold capitalize">
                                        {c.user_type === "company" ? "Unternehmen" : "Kandidat"}
                                    </span>
                                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${s.color}`}>{s.label}</span>
                                </div>
                            </div>
                            <p className="text-sm text-aimploy-muted leading-relaxed line-clamp-2">{c.message}</p>
                            <div className="text-xs text-aimploy-muted mt-3">{formatDateTime(c.created_at)}</div>
                        </button>
                    );
                })}
                {contacts.length === 0 && <div className="bg-white rounded-2xl p-12 text-center text-aimploy-muted border border-black/5">Keine Kontakte gefunden.</div>}
            </div>
        </div>
    );
}

function LeadsView({ leads, total, search, setSearch, onOpen }) {
    return (
        <div data-testid="admin-leads-view">
            <h1 className="font-display text-4xl lg:text-5xl font-bold mb-2">Leads</h1>
            <p className="text-aimploy-muted mb-8">{total} E-Mail-Captures gesamt · {leads.length} angezeigt</p>
            <SearchBar search={search} setSearch={setSearch} placeholder="Name, E-Mail oder Quelle…" />
            <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-black/5 text-xs uppercase tracking-[0.15em] text-aimploy-muted bg-aimploy-bg">
                                <th className="text-left p-4 font-semibold">Name / E-Mail</th>
                                <th className="text-left p-4 font-semibold">Mail-Status</th>
                                <th className="text-left p-4 font-semibold hidden md:table-cell">Quelle</th>
                                <th className="text-left p-4 font-semibold hidden lg:table-cell">Datum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((l) => {
                                const s = getStatusInfo(LEAD_STATUSES, l.email_status || "pending");
                                return (
                                    <tr
                                        key={l.id}
                                        onClick={() => onOpen(l)}
                                        data-testid={`admin-lead-row-${l.id}`}
                                        className="border-b border-black/5 last:border-0 hover:bg-aimploy-bg cursor-pointer transition-colors"
                                    >
                                        <td className="p-4">
                                            <div className="font-semibold">{l.name || "—"}</div>
                                            <div className="text-xs text-aimploy-muted">{l.email}</div>
                                        </td>
                                        <td className="p-4">
                                            <span className={`inline-block text-xs px-2.5 py-1 rounded-full font-semibold ${s.color}`}>
                                                {s.label}
                                            </span>
                                        </td>
                                        <td className="p-4 hidden md:table-cell">
                                            <span className="text-xs px-2.5 py-1 rounded-full bg-aimploy-bg font-medium">
                                                {l.source}
                                            </span>
                                        </td>
                                        <td className="p-4 text-aimploy-muted hidden lg:table-cell">{formatDateTime(l.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {leads.length === 0 && <div className="p-12 text-center text-aimploy-muted">Keine Leads gefunden.</div>}
                </div>
            </div>
        </div>
    );
}

function JobsView({ jobs, search, setSearch, onOpen, onNew }) {
    return (
        <div data-testid="admin-jobs-view">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <h1 className="font-display text-4xl lg:text-5xl font-bold">Stellen</h1>
                <button
                    onClick={onNew}
                    data-testid="admin-new-job"
                    className="inline-flex items-center gap-2 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                >
                    <Plus className="w-4 h-4" /> Neue Stelle
                </button>
            </div>
            <p className="text-aimploy-muted mb-8">{jobs.length} Stellen verwaltet.</p>
            <SearchBar search={search} setSearch={setSearch} placeholder="Titel, Standort, Tag…" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {jobs.map((j) => {
                    const published = j.is_published !== false;
                    return (
                        <button
                            key={j.id}
                            onClick={() => onOpen(j)}
                            data-testid={`admin-job-row-${j.id}`}
                            className="text-left bg-white rounded-2xl p-6 border border-black/5 hover:border-aimploy-violet/30 hover:-translate-y-0.5 transition-all relative"
                        >
                            <div className="flex items-start justify-between mb-3 gap-3">
                                <div className="flex flex-wrap gap-2">
                                    {(j.tags || []).slice(0, 3).map((t) => (
                                        <span key={t} className="text-xs px-2.5 py-0.5 rounded-full bg-aimploy-violet/10 text-aimploy-violet font-semibold">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <span
                                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${
                                        published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                                    }`}
                                >
                                    {published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                    {published ? "Live" : "Entwurf"}
                                </span>
                            </div>
                            <h3 className="font-display text-xl font-bold mb-2">{j.title}</h3>
                            <div className="flex flex-wrap gap-3 text-xs text-aimploy-muted mb-3">
                                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                                <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {j.type}</span>
                            </div>
                            {j.salary_range && <div className="font-display font-bold text-aimploy-navy">{j.salary_range}</div>}
                        </button>
                    );
                })}
            </div>
            {jobs.length === 0 && (
                <div className="bg-white rounded-2xl p-12 text-center text-aimploy-muted border border-black/5">
                    Keine Stellen gefunden.{" "}
                    <button onClick={onNew} className="text-aimploy-violet font-semibold hover:underline">Erste anlegen</button>
                </div>
            )}
        </div>
    );
}
